/**
 * Client-side branch name check — a fast NO, never the authoritative YES.
 *
 * The daemon runs the real `git check-ref-format` before it inserts anything
 * (`src/server.ts`, POST /conversations, and the rename path via
 * `renameWorktreeBranch`). This mirror exists only so a typo is caught in the
 * form instead of after a round trip that opens a column onto a branch git will
 * refuse. Because the server is the authority, the only dangerous error here is
 * over-rejection: refusing a name git would have accepted makes a legal branch
 * untypable, and no server check can undo that.
 *
 * Every rule below is one `git check-ref-format --branch` documents. The set was
 * verified by running both `git check-ref-format --branch <n>` and
 * `git check-ref-format refs/heads/<n>` over a 72-name corpus (valid, invalid
 * and adversarial) and asserting this function agrees on all of them — that run
 * is what caught two wrong rules: git ACCEPTS a branch literally named "@" (the
 * "cannot be @" rule is about the whole ref, not one component) and REJECTS
 * "HEAD". Re-run that comparison before changing anything here.
 */
export function isValidBranchName(name: string): boolean {
  if (name === "" || name === "HEAD") return false;
  // A leading "-" would be read as an option by git; a leading/trailing "/"
  // makes an empty path component.
  if (name.startsWith("-") || name.startsWith("/") || name.endsWith("/")) return false;
  if (name.endsWith(".")) return false;
  // "@{" is reflog syntax ("@{-1}" resolves to some previous branch), so a name
  // containing it would not stay the literal string the user typed.
  if (name.includes("..") || name.includes("//") || name.includes("@{")) return false;
  // Control characters, space (0x20), DEL, and git's reserved glob/revision
  // characters.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x20\x7f~^:?*[\\]/.test(name)) return false;
  // No path component may be empty, start with "." or end with ".lock".
  return name.split("/").every((seg) => seg !== "" && !seg.startsWith(".") && !seg.endsWith(".lock"));
}
